import { Box, Grid } from "@mui/material";
import { useFormik } from "formik";
import * as Yup from "yup";
import { LoadingButton } from "@mui/lab";
import FormikControl from "../../../../components/common/FormikControl";
import { useDispatch, useSelector } from "react-redux";
import { fetchAllRoles, selectAllRoles } from "../../../../slices/roles";
import { useEffect } from "react";
import { addOnboardUser, clearPhaseList, editOnboardUser, fetchHospitalList, fetchPhaseList, fetchProductList, selectBmrAllHospitalList, selectBmrAllOnBoardUser, selectBmrAllPhaseList, selectBmrAllProductList, selectBmrOnBoardUserLoading } from "../../../../slices/onBoardUser";
import { NAME_REGEX } from "../../../../../utils/common";

const validationSchema = Yup.object().shape({
    name: Yup.string()
        .min(1, "User Name must be between 1-50 characters long")
        .max(50, "User Name must be between 1-50 characters long")
        .matches(NAME_REGEX, "Enter a valid Name")
        .required("Name is required"),
    email: Yup.string()
        .email("Must be a valid email")
        .max(255)
        .required("Email is required"),
    phone: Yup.string()
        .matches(/^[6-9]/, "Phone number must start from digits 6-9")
        .max(10, "Phone Number must be 10 digits")
        .min(10, "Phone Number must be 10 digits")
        .required("Phone number is required"),
    role_id: Yup.number().required("Type is required"),
});

const AddNewUserForm = ({ editId, filters }) => {
    const dispatch = useDispatch()
    const allRoles = useSelector(selectAllRoles)
    const allOnBoardUser = useSelector(selectBmrAllOnBoardUser)
    const allHospitalList = useSelector(selectBmrAllHospitalList)
    const allPhaseList = useSelector(selectBmrAllPhaseList)
    const allProductList = useSelector(selectBmrAllProductList)
    const isLoading = useSelector(selectBmrOnBoardUserLoading)
    const editUser = editId ? allOnBoardUser.items?.find(user => user.id == editId) : undefined

    const initialValues = {
        role_id: editUser?.role_details?.id ?? "",
        name: editUser?.name ?? "",
        phone: editUser?.phone ?? "",
        email: editUser?.email ?? "",
        product_id: editUser?.product_id ?? [],
        phase_ids: editUser?.phase_user.map(op => op.id) ?? [],
        hospital_id: editUser?.hospital_details?.id ?? ""
    };
    const handleSubmit = async (values) => {
        if (editId) {
            await dispatch(editOnboardUser({ data: { ...values, phone: values.phone.toString(), country_code: "+91" }, editId, filters }))
        } else {
            await dispatch(addOnboardUser({ data: { ...values, phone: values.phone.toString(), country_code: "+91" }, filters }))
        }
    }
    const formik = useFormik({
        initialValues,
        validationSchema,
        onSubmit: handleSubmit,
        enableReinitialize: true
    })

    useEffect(() => {
        getRoles()
    }, [])

    const getRoles = async () => {
        await dispatch(fetchProductList())
        if (!editId) {
            await dispatch(clearPhaseList()); 
        }
        await dispatch(fetchAllRoles())
        await dispatch(fetchHospitalList())
    }

    useEffect(() => {
        const getPhaseList = async () => {
            if (formik.values.product_id?.length > 0) {
                await dispatch(fetchPhaseList({product_id:formik.values.product_id.join(","),role_id:formik.values.role_id}));
            } else {
                if (!editId) {
                    await dispatch(clearPhaseList());
                }
            }
        };

        if (allProductList?.length > 0) {
            getPhaseList();
        }
    }, [formik.values.product_id, allProductList, dispatch, editId]);
       
    // remove leading spaces & handle null safely
    const handleChange = (e, field) => {
        let value = e.target.value;
        value = value.replace(/^\s+/, "");
        formik.setFieldValue(field, value)
    }
    
    const formOptions = [
        {
            label: 'User Type',
            type: 'select',
            name: 'role_id',
            required: true,
            disabled: editId ? true : false,
            placeholder: "Select type",
            options: allRoles.map(list => { return { value: list.id, label: list.title } }),
        },
        {
            label: 'Name',
            type: 'text',
            name: 'name',
            required: true,
            placeholder: 'Enter name',
            onChange: (e) => handleChange(e, "name")
        },
        {
            label: 'Phone',
            type: 'number',
            name: 'phone',
            required: true,
            placeholder: 'Enter number'
        },
        {
            label: 'Email',
            type: 'email',
            name: 'email',
            required: true,
            disabled: editId,
            placeholder: 'Enter email'
        },
        {
            label: 'Hospital',
            type: 'select',
            name: 'hospital_id',
            placeholder: "Select hospital",
            options: allHospitalList?.map(list => { return { value: list.id, label: list?.name + ' ' + `(${list?.city_name})` } }),
        },
        {
            label: 'Product',
            type: 'select',
            multiple: true,
            name: 'product_id',
            placeholder: "Select Product",
            options: allProductList?.map(list => { return { value: list.id, label: list?.name } }),
            onChange: (e) => {
                const { value } = e.target;
                const selectedProducts = Array.isArray(value) ? value : [value];
                const validPhaseIds = formik.values.phase_ids.filter(phaseId => {
                    const phase = allPhaseList.find(p => p.id === phaseId);
                    return phase && selectedProducts.includes(phase.product_details?.id);
                });
                formik.setFieldValue("product_id", selectedProducts);
                formik.setFieldValue("phase_ids", validPhaseIds);
            }
        },
        {
            label: 'Phase',
            type: 'select',
            name: 'phase_ids',
            multiple: true,
            placeholder: "Select phase",
            options: allPhaseList?.map(list => { return { value: list.id, label: `${list?.title} (${list?.product_details?.name})` } }),
        },
    ];


    return (
        <Box>
            <form onSubmit={formik.handleSubmit}>
                <Grid container spacing={2} className="mb-3">
                    {formOptions.map((props, key) => (
                        <Grid key={key} item xs={12}>
                            <FormikControl formik={formik} {...props} />
                        </Grid>
                    ))}
                </Grid>
                <Grid container spacing={2} className="mt-3">
                    <Grid item xs={12}>
                        <LoadingButton loading={isLoading} fullWidth type="submit" variant="contained">{editId ? "Update" : "Submit"}</LoadingButton>
                    </Grid>
                </Grid>
            </form>
        </Box>
    );
}

export default AddNewUserForm;